import { template as template_3b31877c869743f384d7eef2019edf94 } from "@ember/template-compiler";
import { filteredOfficeIdsForRecord } from 'client-portal/utils/clinicianSearch/send-all-catalog';
import { fn } from '@ember/helper';
import { formatCardNamedList, formatCardPaymentList } from 'client-portal/utils/clinicianSearch/card-named-list';
import { humanDateFormat } from 'client-portal/utils/date-time';
import { locationLineFor, officeModalityFlags } from 'client-portal/utils/clinicianSearch/availability-display';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import AvatarImgPath from 'client-portal/helpers/avatar-img-path';
import Component from '@glimmer/component';
import RequestClinicianSearchMarketingInfoList from './marketing-info-list';
import SpdsPrimaryButton from 'ember-simplepractice/components/spds/action/button/primary';
import momentFormat from 'client-portal/helpers/moment-format';
import styles from './card.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import t from 'ember-intl/helpers/t';
export default class RequestClinicianSearchCard extends Component {
    @service
    intl;
    @service
    currentPractice;
    @service
    session;
    @service
    clinicianSearch;
    @service
    store;
    styles = styles;
    get clinician() {
        return this.args.model;
    }
    // 'pending' before the next-slots POST resolves; 'ready' after the
    // response (whether value or null) has been merged. Undefined while
    // the catalog has loaded but no enrichment has been kicked off yet —
    // treated like 'pending' so the skeleton shows.
    get enrichmentStatus() {
        let status = this.clinicianSearch.nextSlots.statusFor(this.clinician.id);
        return status ?? 'pending';
    }
    get isEnrichmentPending() {
        return this.enrichmentStatus === 'pending';
    }
    get isCatalogLoaded() {
        return this.clinicianSearch.sendAll.isLoaded;
    }
    get credentialLine() {
        return this.clinician.profileTitle?.trim() || this.clinician.globalProfessionName?.trim();
    }
    get specialtiesLine() {
        if (!this.clinicianSearch.isDiscoveryEnabled) {
            return null;
        }
        return formatCardNamedList(this.clinician.marketingSpecialties);
    }
    get paymentLine() {
        if (!this.clinicianSearch.isDiscoveryEnabled) {
            return null;
        }
        return formatCardPaymentList(this.clinician.marketingInsuranceProviders, {
            acceptSelfPay: this.clinician.acceptSelfPayCashIndicator,
            selfPayLabel: this.intl.t('booking.clinicianSearch.card.selfPay')
        });
    }
    get dominantOffice() {
        let id = this.clinician.dominantOfficeId;
        return id ? this.store.peekRecord('office', id) : null;
    }
    // Offices the clinician has availability at under the active filters. Drives
    // the in-person/telehealth modality so a clinician with both physical and
    // video offices reads as "Hybrid", while an office/cpt/day/time filter
    // narrows the set the modality is computed from.
    get availableOffices() {
        let ids = filteredOfficeIdsForRecord(this.clinician, this.clinicianSearch.query?.toRouteParams() ?? {});
        return this.getModelFromIds(ids, 'office');
    }
    getModelFromIds(ids, modelName) {
        return ids?.map((id)=>this.store.peekRecord(modelName, id)).filter(Boolean) ?? null;
    }
    get modality() {
        return officeModalityFlags({
            availableOffices: this.availableOffices
        });
    }
    get locationOfficesLine() {
        return locationLineFor({
            dominantOffice: this.dominantOffice,
            availableOffices: this.availableOffices,
            practice: this.currentPractice,
            intl: this.intl
        });
    }
    get notAcceptingNewClients() {
        return !this.clinician.acceptNewClientsIndicator;
    }
    get hasNextAvailability() {
        return !!this.clinician.nextAvailability;
    }
    get hasLaterAvailability() {
        return (!this.isEnrichmentPending && !this.hasNextAvailability && !!this.clinician.availabilityBlocks?.length);
    }
    get hasAvailabilityError() {
        return !!this.args.meta?.availability?.errorCode;
    }
    get isSelectDisabled() {
        if (this.isEnrichmentPending) {
            return true;
        }
        let canCheckAvailability = this.hasNextAvailability || this.hasLaterAvailability || this.hasAvailabilityError;
        let canSelectAsNewClient = this.clinician.acceptNewClientsIndicator;
        return !(canCheckAvailability && canSelectAsNewClient);
    }
    get showInPersonBadge() {
        return this.isCatalogLoaded && this.modality.hasInPerson;
    }
    get showTelehealthBadge() {
        return this.isCatalogLoaded && this.modality.hasTelehealth;
    }
    get showNoUpcomingAvailability() {
        return !this.isEnrichmentPending && !this.hasNextAvailability && !this.hasLaterAvailability;
    }
    static{
        template_3b31877c869743f384d7eef2019edf94(`
    <div class={{styles.component}} data-test-clinician-search-card>
      <div class="avatar" aria-hidden="true">
        {{#if @model.profileImageUrl}}
          <img class="avatar-img" src={{@model.profileImageUrl}} alt={{@model.fullName}} />
        {{else}}
          <img class="avatar-pattern" src={{AvatarImgPath @model.id}} alt="" />
          <div class="avatar-initials">
            {{@model.initials}}
          </div>
        {{/if}}
      </div>

      <div class="details">
        <div class="name" data-test-clinician-search-card-name>
          {{@model.fullName}}
        </div>

        {{#if this.credentialLine}}
          <div class="credential-line" data-test-clinician-search-card-credential>
            {{this.credentialLine}}
          </div>
        {{/if}}

        <div class="location-offices-line" data-test-clinician-search-card-location-offices>
          {{#if this.isCatalogLoaded}}
            {{this.locationOfficesLine}}
          {{else}}
            <span>{{t "booking.clinicianSearch.card.loadingOffices"}}</span>
          {{/if}}
        </div>

        <div class="badges" data-test-clinician-search-card-badges>
          {{#if this.notAcceptingNewClients}}
            <span
              class="badge badge-not-accepting badge-with-icon"
              data-test-clinician-search-card-not-accepting
            >
              {{svgJar "not-accepting" class="badge-icon" aria-hidden="true"}}
              {{t "booking.clinicianSearch.card.notAccepting"}}
            </span>
          {{/if}}
          {{#if this.showInPersonBadge}}
            <span
              class="badge badge-modality badge-with-icon"
              data-test-clinician-search-card-in-person
            >
              {{svgJar "chair" class="badge-icon" aria-hidden="true"}}
              {{t "booking.clinicianSearch.card.inPerson"}}
            </span>
          {{/if}}
          {{#if this.showTelehealthBadge}}
            <span
              class="badge badge-modality badge-with-icon"
              data-test-clinician-search-card-telehealth
            >
              {{svgJar "telehealth-pill" class="badge-icon" aria-hidden="true"}}
              {{t "booking.clinicianSearch.card.telehealth"}}
            </span>
          {{/if}}
        </div>

        <RequestClinicianSearchMarketingInfoList
          @line={{this.specialtiesLine}}
          @icon="specialties-star"
          data-test-clinician-search-card-specialties
        />

        <RequestClinicianSearchMarketingInfoList
          @line={{this.paymentLine}}
          @icon="insurance-shield"
          data-test-clinician-search-card-payment
        />
      </div>

      <div class="card-footer">
        {{#if this.hasNextAvailability}}
          <div class="next-availability" data-test-clinician-search-card-next-availability>
            {{svgJar "event-available" class="next-availability-icon" aria-hidden="true"}}
            <span class="next-availability-text">
              <span class="next-label">{{t "booking.clinicianSearch.card.nextAvailability"}}</span>
              <span class="next-value">
                {{momentFormat this.clinician.nextAvailability humanDateFormat}}
              </span>
            </span>
          </div>
        {{else if this.isEnrichmentPending}}
          <div
            class="next-availability is-loading"
            data-test-clinician-search-card-next-availability-loading
            aria-live="polite"
          >
            <span class="next-availability-skeleton" aria-hidden="true"></span>
            <span class="next-availability-text">
              <span class="next-label">{{t
                  "booking.clinicianSearch.card.nextAvailabilityLoading"
                }}</span>
            </span>
          </div>
        {{else if this.hasLaterAvailability}}
          <div
            class="next-availability is-empty"
            data-test-clinician-search-card-next-availability-later
          >
            {{svgJar "event-available" class="next-availability-icon" aria-hidden="true"}}
            <span class="next-availability-text">
              <span class="next-label">{{t
                  "booking.clinicianSearch.card.availableAfterThirtyDays"
                }}</span>
            </span>
          </div>
        {{else if this.showNoUpcomingAvailability}}
          <div
            class="next-availability is-empty"
            data-test-clinician-search-card-next-availability-empty
          >
            {{svgJar "event-available" class="next-availability-icon" aria-hidden="true"}}
            <span class="next-availability-text">
              <span class="next-label">{{t
                  "booking.clinicianSearch.card.noUpcomingAvailability"
                }}</span>
            </span>
          </div>
        {{/if}}

        <SpdsPrimaryButton
          @text={{t "booking.service.select"}}
          @disabled={{this.isSelectDisabled}}
          aria-disabled={{if this.isSelectDisabled "true"}}
          class="button primary select"
          data-test-clinician-search-card-select
          {{on "click" (fn @nextStep @model null)}}
        />
        {{#if this.isSelectDisabled}}
          <div class="tooltip-container">
            <AttachTooltip
              @placement="right"
              @class="tooltip align-left filter-disabled-tooltip-popover"
              data-test-clinician-search-card-select-tooltip
            >
              {{t "booking.clinicianSearch.card.selectDisabled"}}
            </AttachTooltip>
          </div>
        {{/if}}
      </div>
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
